// @flow

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PageMenu from "../../../components/custom/app/pageMenu/pageMenu";
import PageTitle from "../../../components/mui/app/pageTitle/pageTitle";
import PageContentWrapper from "../../../components/mui/general/pageContentWrapper/pageContentWrapper";
import { CustomPagination } from "../../../components/mui/grids/dataGridCustom/customPagination";
import DataGridWrapper from "../../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { muiGridLocales } from "../../../helpers/mui/muiLocales";
import { access_projects } from "../../../helpers/rights";
import { getRouteUrl } from "../../../helpers/routing/getRouteUrl";
import { useMuiGridServerSide } from "../../../hooks/muiGrid/useMuiGridServerSide";
import useUserData from "../../../hooks/user/useUserData";
import { withPermissionHOC } from "../../permission/components/permissionHOC/permissionHOC";
import { PROJECTS } from "../locales/namespaces";
import {
	DataGridPremium,
	GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
	GridActionsCellItem,
	GridToolbar
} from '@mui/x-data-grid-premium'
import { ROUTE_PAGE_PROJECT_DETAILS, ROUTE_PAGE_PROJECT_EDIT } from "../routes/routes";

type Props = {};

const Index = (props: Props) => {
	const { t } = useTranslation(PROJECTS);
	useEffect(() => {
		document.title = t('Projects List')
	}, [])
	
	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange
	} = useMuiGridServerSide({
		url: 'fts/projects/index',
		initialPageSize: 50,
		initialSortModel: [{ name: 'id', direction: 'descending' }]
	})
	const { language, dateFormat } = useUserData()
	
	const columns = useMemo(() => {
		return [
			{
				headerName: t('Actions'),
				field: 'actions',
				type: 'actions',
				getActions: (params) => {
					if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
						return []
					}
					return [
						<GridActionsCellItem
							component={Link}
							to={getRouteUrl(ROUTE_PAGE_PROJECT_EDIT, { projectId: params.id })}
							icon={
								<Tooltip title={t('Edit')}>
									<IconButton>
										<EditIcon/>
									</IconButton>
								</Tooltip>
							}
							color={'info'}
							label={t('Edit')}>
						</GridActionsCellItem>
					]
				}
			},
			{
				field: 'Description',
				headerName: t('Description'),
				minWidth: 300,
				flex: 1,
				renderCell: ({ row }) => (
					<Link to={getRouteUrl(ROUTE_PAGE_PROJECT_DETAILS, {
						projectId: row.id
					})}>
						{row.Description}
					</Link>
				)
			},
			// {
			// 	field: 'LoginName',
			// 	headerName: t('Foreman'), //Εργοδηγός
			// 	minWidth: 200, flex: 1,
			// },
			{
				field: 'StartDt',
				headerName: t('Start'),
				minWidth: 180, flex: 1,
				align:'center',
				valueFormatter: (value, row, column, apiRef) => {
					return (value ? dayjs(value).format(dateFormat) : null)
				},
				valueGetter: (value, row, column, apiRef) => {
					return value && new Date(value)
				}
			},
			{
				field: 'EndDt',
				headerName: t('End'),
				minWidth: 180, flex: 1,
				align:'center',
				valueFormatter: (value, row, column, apiRef) => {
					return (value ? dayjs(value).format(dateFormat) : null)
				},
				valueGetter: (value, row, column, apiRef) => {
					return value && new Date(value)
				}
			},
			{
				field: 'EstimCost',
				headerName: t('Estim. Cost'),
				minWidth: 200, flex: 1,
				align: 'right',
				valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			},
			{
				field: 'WithContributions',
				headerName: t('With Contribution'),
				minWidth: 200, flex: 1,
				align: 'right',
				valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			},
			{
				field: 'TotalCost',
				headerName: t('Total Cost'),
				minWidth: 200, flex: 1,
				align: 'right',
				valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			},
			// {
			// 	field: 'EstimDt',
			// 	headerName: t('Est. Date'),
			// 	minWidth: 180, flex: 1,
			// 	valueFormatter: (value, row, column, apiRef) => {
			// 		return (value ? dayjs(value).format('DD/MM/YYYY') : null)
			// 	},
			// 	valueGetter: (value, row, column, apiRef) => {
			// 		return value && new Date(value)
			// 	}
			// },
			{
				field: 'isActive',
				headerName: t('Active'),
				minWidth: 150, flex: 1,
				type: 'boolean',
				valueGetter: (value, row, column, apiRef) => value === '1'
			}
		]
	}, [t, dateFormat])
	return (
		<Box className={'projects_index'} sx={{ marginTop: '10px' }}>
			
			<PageMenu>
				<Button
					component={Link}
					to={'/project/create'}
					startIcon={<AddCircleOutlineIcon/>}
				>
					{t('Add')}
				</Button>
			
			</PageMenu>
			<PageContentWrapper>
				<PageTitle
					title={t('Projects')}/>
				<DataGridWrapper sx={{ height: 700 }}>
					<DataGridPremium
						columns={columns}
						initialState={{ density: "compact" }}
						rows={data}
						pagination
						paginationModel={{ page, pageSize }}
						paginationMode="server"
						onPaginationModelChange={onPaginationModelChange}
						rowCount={Number(total)}
						pageSizeOptions={[5, 10, 15, 20, 50, 100]}
						sortingMode="server"
						onSortModelChange={(sortModel) => onSortModelChange(sortModel)}
						filterMode="server"
						onFilterModelChange={onFilterModelChange}
						loading={isLoading}
						localeText={muiGridLocales(language)}
						slots={{
							pagination: CustomPagination,
							toolbar: GridToolbar,
						}}
					/>
				</DataGridWrapper>
			</PageContentWrapper>
		</Box>
	);
};

export default withPermissionHOC(Index, [access_projects])